import React, { useEffect, useState } from 'react';
import NewsEditor from './NewsEditor';
import styles from './newsManager.module.css';
import { useCheckPermission } from '../../adminPanelTools';
import { showErrorToast, showInfoToast } from '../../../../code/ToastHelper';
import { usePermissionGuard } from '../../adminPanelTools';
function NewsManager() {
    const checking = usePermissionGuard('handle_news');
    const [newsList, setNewsList] = useState([]);
    const [showEditorModal, setShowEditorModal] = useState(false);
    const [editNews, setEditNews] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const { hasPermission: canSetAnyAuthor, checking: checkingPermission } = useCheckPermission('set_any_author');

    const [limit] = useState(10);
    const [offset, setOffset] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [onlyHighlighted, setOnlyHighlighted] = useState(false);
    const [totalNewsCount, setTotalNewsCount] = useState(null);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    useEffect(() => {
        fetchCurrentUser();
    }, []);

    useEffect(() => {
        if (currentUser && !checkingPermission) {
            fetchNews();
        }
    }, [currentUser, canSetAnyAuthor, checkingPermission, offset, searchTerm, onlyHighlighted, dateFrom, dateTo]);

    const fetchCurrentUser = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.warn('No token found in localStorage');
            return;
        }

        const res = await fetch('/api/auth/me', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            console.error('Failed to fetch current user');
            return;
        }

        const data = await res.json();
        setCurrentUser(data);
    };

    const fetchNews = async () => {
        try {
            let endpoint = '';

            const params = new URLSearchParams({
                limit,
                offset,
                search: searchTerm,
            });
            if (dateFrom) params.append('date_from', dateFrom);
            if (dateTo) params.append('date_to', dateTo);

            const token = localStorage.getItem('token'); // ⬅️ Get token from storage

            if (canSetAnyAuthor) {
                if (onlyHighlighted) params.append('is_highlighted', '1');
                endpoint = `/api/news/get_all_news?${params.toString()}`;
            } else {
                endpoint = `/api/news/get_user_news?${params.toString()}`;
            }

            const res = await fetch(endpoint, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`, // ⬅️ Include token here
                },
            });

            if (!res.ok) throw new Error();

            const data = await res.json();
            setNewsList(data.articles || data);

            if (data.totalCount !== undefined) {
                setTotalNewsCount(data.totalCount);
            } else {
                setTotalNewsCount(null);
            }
        } catch (err) {
            showErrorToast('Fehler beim Laden der News');
        }
    };

    const handleCreate = () => {
        setEditNews(null);
        setShowEditorModal(true);
    };

    const handleEdit = (newsItem) => {
        setEditNews(newsItem);
        setShowEditorModal(true);
    };

    const handleDelete = async (news) => {
        if (!window.confirm('Möchtest du diesen Artikel wirklich löschen?')) return;
        const res = await fetch(`/api/news/${news.article_id}`, { method: 'DELETE' });
        if (!res.ok) {
            showErrorToast('Fehler beim Löschen');
            return;
        }
        fetchNews();
        showInfoToast(`News: ${news.title} wurde erfolgreich gelöscht!`);
    };

    const handleEditorClose = (refresh = false) => {
        setShowEditorModal(false);
        setEditNews(null);
        if (refresh) fetchNews();
    };

    return (
        <div className={styles.container}>
            <h1>News verwalten</h1>

            <button onClick={handleCreate} className={styles.createButton}>
                Neue News erstellen
            </button>

            <div className={styles.controls}>
                <div className={styles.upperRow}>
                    <input
                        type="text"
                        placeholder="Nach Titel suchen..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />

                    {/* Date From */}
                    <label>
                        Von:
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className={styles.dateInput}
                        />
                    </label>

                    {/* Date To */}
                    <label>
                        Bis:
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className={styles.dateInput}
                        />
                    </label>

                    {canSetAnyAuthor && (
                        <label>
                            <input
                                type="checkbox"
                                checked={onlyHighlighted}
                                onChange={(e) => setOnlyHighlighted(e.target.checked)}
                            />
                            Nur Highlights anzeigen
                        </label>
                    )
                    }
                </div>
                <div className={styles.lowerRow}>
                    <button
                        onClick={() => setOffset((prev) => Math.max(prev - limit, 0))}
                        disabled={offset === 0}
                    >
                        &lt; Neuere News
                    </button>
                    <button
                        onClick={() => setOffset((prev) => prev + limit)}
                        disabled={totalNewsCount !== null && offset + limit >= totalNewsCount}
                    >
                        Ältere News &gt;
                    </button>
                </div>

            </div>
            <ul className={styles.newsList}>
                {newsList.map((news) => (
                    <li key={news.article_id} className={styles.newsItem}>
                        {news.image && (
                            <img
                                src={news.image}
                                alt={news.title}
                                className={styles.newsImage}
                            />
                        )}
                        <div className={styles.newsContent}>
                            <h3 className={styles.newsTitle}>{news.title}</h3>
                            <p className={styles.newsMeta}>
                                <span>📅 {news.published_date}</span>
                                <span>✍️ {news.author_name}</span>
                                {news.is_highlighted ? <span className={styles.highlightTag}>Highlight</span> : null}
                            </p>
                            <p className={styles.newsSlug}>Slug: <code>{news.slug}</code></p>
                        </div>
                        <div className={styles.newsActions}>
                            <button onClick={() => handleEdit(news)} className={styles.editButton}>Bearbeiten</button>
                            <button onClick={() => handleDelete(news)} className={styles.deleteButton}>Löschen</button>
                        </div>
                    </li>
                ))}
            </ul>

            {showEditorModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <NewsEditor
                            initialData={editNews}
                            onClose={handleEditorClose}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default NewsManager;
